// per docs/shape-ops-no-report-exceptions.md Decision 2, Decision 6 (T110)
// per docs/caiet-uc-spec.md §UC-ALRT-13; register CF-56
// The day and Registrar-type exceptions of a missing-report rule, as the form holds
// them and as the expression carries them.
import { formatDate } from '@simsm/ui-shared';
import { NON_WORKING_WEEKDAYS, REGISTRAR_KINDS, type RegistrarKind } from '@simsm/shared-types';

/** ISO weekdays, Monday first: the order the form lists them in. */
export const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

export interface NoReportExceptions {
  weekdays: number[];
  dates: string[];
  registrarKinds: RegistrarKind[];
}

/** A new rule starts from the caiet's initial configuration: Saturday and Sunday. */
export function defaultNoReportExceptions(): NoReportExceptions {
  return { weekdays: [...NON_WORKING_WEEKDAYS], dates: [], registrarKinds: [] };
}

function listOf(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function isRegistrarKind(value: unknown): value is RegistrarKind {
  return (REGISTRAR_KINDS as readonly unknown[]).includes(value);
}

/** Absent weekdays mean the initial configuration, as the evaluator reads them. */
export function noReportExceptionsFromParams(params: Record<string, unknown>): NoReportExceptions {
  return {
    weekdays:
      params['exceptWeekdays'] === undefined
        ? [...NON_WORKING_WEEKDAYS]
        : listOf(params['exceptWeekdays']).filter((day): day is number => typeof day === 'number'),
    dates: listOf(params['exceptDates']).filter((day): day is string => typeof day === 'string'),
    registrarKinds: listOf(params['exceptRegistrarKinds']).filter(isRegistrarKind),
  };
}

export function noReportExceptionsToParams(
  exceptions: NoReportExceptions,
): Record<string, unknown> {
  return {
    exceptWeekdays: ISO_WEEKDAYS.filter((day) => exceptions.weekdays.includes(day)),
    exceptDates: [...new Set(exceptions.dates)].sort((a, b) => a.localeCompare(b)),
    exceptRegistrarKinds: REGISTRAR_KINDS.filter((kind) =>
      exceptions.registrarKinds.includes(kind),
    ),
  };
}

/** With every weekday excepted the rule could never fire; the evaluator refuses it. */
export function everyWeekdayExcepted(exceptions: NoReportExceptions): boolean {
  return ISO_WEEKDAYS.every((day) => exceptions.weekdays.includes(day));
}

/**
 * The detail page's reading of a parameter it would otherwise print as codes: the
 * weekdays and Registrar types by name, the dates as dates. `undefined` for any other.
 */
export function describeNoReportParam(
  key: string,
  value: unknown,
  translate: (key: string, fallback: string) => string,
): string | undefined {
  switch (key) {
    case 'exceptWeekdays':
      return listOf(value)
        .map((day) => translate(`alerts.weekday.${String(day)}`, String(day)))
        .join(', ');
    case 'exceptDates':
      return listOf(value)
        .map((day) => formatDate(String(day)))
        .join(', ');
    case 'exceptRegistrarKinds':
      return listOf(value)
        .map((kind) => translate(`registrars.kind.${String(kind)}`, String(kind)))
        .join(', ');
    default:
      return undefined;
  }
}
